import { useEffect, useMemo, useRef } from 'react'
import { useValuationStore } from '@/store/valuationStore'
import { mapResponseToForm } from '@/utils/fieldResponseMapper'
import { matchDropdownValue } from '@/utils/matchDropdownValue'
import { OCR_CONFIDENCE_THRESHOLD } from '@/services/ocrService'
import { useRefStore } from '@/store/refStore'
import { useAttributeStore } from '@/store/attributeStore'
import { useRefData } from '@/hooks/useRefData'
import { useAttributeData } from '@/hooks/useAttributeData'
import { SECTIONS } from '@/data/sections.data'
import {
  TRUCK_STANDARD_FIELDS,
  TRAILER_STANDARD_FIELDS,
  TRUCK_ADDITIONAL_FIELDS,
  TRAILER_ADDITIONAL_FIELDS,
  TRUCK_CONDITIONAL_FIELDS,
  TRAILER_CONDITIONAL_FIELDS,
  TRUCK_USE_SUBTYPE_FIELD,
  TRAILER_TYPE_SUBTYPE_FIELD,
} from '@/data/conditionalFields.data'
import {
  BASIC_INFO_API_FIELDS,
  getBaseCategoriesForSection,
  resolveFieldCategory,
  normaliseToKey,
  type CategoryDeps,
} from '@/data/refCategories'
import { REF_KEYS } from '@/data/refKeys'
import { mapAttributesToFields } from '@/utils/attributeMapper'
import { sortSelectOptions } from '@/utils'
import type { FieldDefinition } from '@/types'
import type { RefOption } from '@/services/refService'

// ── Option augmentation ───────────────────────────────────────────────────────

/**
 * Determines whether a select field should have its options replaced with
 * API data.
 *
 * Criteria:
 *   - The field is a select whose options are exactly ['Select']
 *     (the placeholder sentinel used throughout conditionalFields.data.ts), OR
 *   - The field is explicitly listed in BASIC_INFO_API_FIELDS (e.g. typeOfLoss)
 */
function isDynamicField(field: FieldDefinition, sectionId: string): boolean {
  if (field.type !== 'select') return false

  const isPlaceholderOnly =
    field.options?.length === 1 &&
    field.options[0] === 'Select'

  const isBasicInfoOverride =
    sectionId === 'basic-information' && field.id in BASIC_INFO_API_FIELDS

  return isPlaceholderOnly || isBasicInfoOverride
}

/**
 * Returns an augmented copy of a field with API-fetched options merged in.
 * Falls back to the original field (showing placeholder) when:
 *   - The field is not dynamic
 *   - The resolved category is null (required parent not yet selected)
 *   - The category has not been loaded yet
 */
// model/noOfSpeeds → their IDs are saved to DB via toRefId; must resolve through the
// shared NOT_IN_LIST_MODEL category (one sentinel row reused across every ID-backed
// dependent field), not a hardcoded string.
const REF_BACKED_DEPENDENT: Record<string, keyof CategoryDeps> = {
  model: 'make',
  noOfSpeeds: 'transMfg',
}

// engineModel/transModel → stored as plain text in DB; hardcoded "Not in List" is fine.
const TEXT_DEPENDENT: Record<string, keyof CategoryDeps> = {
  engineModel: 'engineMfg',
}

function augmentFieldOptions(
  field: FieldDefinition,
  sectionId: string,
  vehicleType: string,
  deps: CategoryDeps,
  refCache: Record<string, RefOption[]>
): FieldDefinition {
  if (!isDynamicField(field, sectionId)) return field

  // Ref-backed dependent: when parent is "Not in List", serve options from the
  // shared NOT_IN_LIST_* category so the child resolves a real ref_master ID on save.
  const refParentKey = REF_BACKED_DEPENDENT[field.id]
  if (refParentKey) {
    const parentVal = deps[refParentKey] ?? ''
    if (parentVal.toLowerCase() === 'not in list') {
      const cached = refCache[REF_KEYS.NOT_IN_LIST_MODEL]
      if (!cached?.length) return field  // category still loading — the dependent-load effect triggered it
      return { ...field, options: sortSelectOptions(['Select', ...cached.map(o => o.label)]) }
    }
  }

  // Text-stored dependent: stored as a plain string in DB, so hardcoding is safe.
  const textParentKey = TEXT_DEPENDENT[field.id]
  if (textParentKey) {
    const parentVal = deps[textParentKey] ?? ''
    if (parentVal.toLowerCase() === 'not in list') {
      return { ...field, options: ['Select', 'Not in List'] }
    }
  }

  // Use the shared ref_category from the attribute definition when available,
  // otherwise fall back to the vehicle-specific derived key.
  const category = field.refCategory ?? resolveFieldCategory(field.id, sectionId, vehicleType, deps)
  if (!category) return field

  const cached = refCache[category]
  if (!cached?.length) return field

  return {
    ...field,
    options: sortSelectOptions(['Select', ...cached.map((o) => o.label)]),
  }
}

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * Returns the resolved + option-augmented fields for a section.
 *
 * For sections whose fields are determined by vehicle type, this hook:
 *   1. Returns the correct FieldDefinition[] based on the current vehicleType.
 *   2. Augments any placeholder-only ('Select') select fields
 *      with live options fetched from the ref_master API.
 *   3. Lazily triggers data loading when the section is expanded, and re-triggers
 *      dependent category loads when parent values change (e.g. Make → Model).
 *
 * For body-information (Truck/Tractor):
 *   4. Fetches FieldDefinition config from attribute_master when Use changes.
 *   5. Resets body-information form values whenever Use changes.
 *   6. Batch-loads ref options for any select fields in the returned attributes.
 */
export function useConditionalFields(sectionId: string): FieldDefinition[] {
  const { formValues, sectionStates, resetSectionFields, setFieldValue, clearRawAIValue, setScanResult, clearScanResult, setAiValue } =
    useValuationStore()
  // Stable string key derived only from rawAIValues in this section. Changes when a new
  // pending AI value arrives; becomes empty after the re-match effect clears them. Using
  // a primitive (not the metadata object) avoids infinite loops from object-ref churn.
  const pendingRawAIKey = useValuationStore((s) => {
    const meta = s.fieldMetadata[sectionId]
    if (!meta) return ''
    return Object.entries(meta)
      .filter(([, m]) => m.rawAIValue)
      .map(([k, m]) => `${k}=${m.rawAIValue}`)
      .sort()
      .join(',')
  })
  const refCache = useRefStore((s) => s.cache)
  const attrCache = useAttributeStore((s) => s.cache)
  const { loadBatch, loadCategory, loadUsageSubtypes } = useRefData()
  const { loadCategory: loadAttributes } = useAttributeData()

  const vehicleType = formValues['basic-information']?.['vehicleType'] ?? ''
  const useValue = formValues['standard-equipment']?.['use'] ?? ''
  const typeValue = formValues['standard-equipment']?.['type'] ?? ''
  const useSubtypeValue = formValues['standard-equipment']?.['useSubtype'] ?? ''
  const typeSubtypeValue = formValues['standard-equipment']?.['typeSubtype'] ?? ''
  const make = formValues['standard-equipment']?.['make'] ?? ''
  const engineMfg = formValues['standard-equipment']?.['engineMfg'] ?? ''
  const transMfg = formValues['standard-equipment']?.['transMfg'] ?? ''

  // Subtype ref category for the current Use/Type — undefined until Use/Type is validly
  // selected. Cache presence (even an empty array) means the live lookup has completed.
  const isValidUse = !!useValue && useValue !== 'Select' && useValue.toLowerCase() !== 'not in list'
  const isValidType = !!typeValue && typeValue !== 'Select' && typeValue.toLowerCase() !== 'not in list'
  const useSubtypeCategory = vehicleType === 'Truck/Tractor' && isValidUse
    ? `TRUCK_USE_SUBTYPE_${normaliseToKey(useValue)}`
    : undefined
  const typeSubtypeCategory = vehicleType === 'Trailer' && isValidType
    ? `TRAILER_TYPE_SUBTYPE_${normaliseToKey(typeValue)}`
    : undefined
  const useSubtypeOptions = useSubtypeCategory ? refCache[useSubtypeCategory] : undefined
  const typeSubtypeOptions = typeSubtypeCategory ? refCache[typeSubtypeCategory] : undefined
  // null = not yet checked (subtype fetch pending/not started), true/false once known
  const hasUseSubtypes = useSubtypeOptions === undefined ? null : useSubtypeOptions.length > 0
  const hasTypeSubtypes = typeSubtypeOptions === undefined ? null : typeSubtypeOptions.length > 0

  const isExpanded = sectionStates[sectionId]?.expanded ?? false

  const deps: CategoryDeps = {
    make, engineMfg, transMfg,
    bodyUse: useValue, bodyType: typeValue,
    bodyUseSubtype: useSubtypeValue, bodyTypeSubtype: typeSubtypeValue,
  }

  // ── Effect 1: batch-load base (non-dependent) categories on section open ──
  useEffect(() => {
    if (!isExpanded) return
    const cats = getBaseCategoriesForSection(sectionId, vehicleType)
    if (cats.length) loadBatch(cats)
    // loadBatch identity is stable within a render cycle; sectionId/vehicleType
    // are the real triggers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded, sectionId, vehicleType])

  // ── Effect 2: dependent load — Model depends on Make ─────────────────────
  const prevMakeRef = useRef<string | null>(null)
  useEffect(() => {
    if (!isExpanded || sectionId !== 'standard-equipment') return
    // Make actually changed (not initial mount/expand) — clear the stale Model
    // value so it doesn't linger as a leftover option not present in the new list.
    if (prevMakeRef.current !== null && prevMakeRef.current !== make) {
      setFieldValue('standard-equipment', 'model', '')
    }
    prevMakeRef.current = make
    const isValidMake = make && make !== 'Select'
    if (!isValidMake) return
    if (make.toLowerCase() === 'not in list') {
      // Load the shared NOT_IN_LIST_MODEL category so the model select gets a real
      // ref_master ID rather than a hardcoded string. Auto-selection happens in the
      // effect below once the category arrives in the cache.
      loadCategory(REF_KEYS.NOT_IN_LIST_MODEL)
      return
    }
    const prefix = vehicleType === 'Truck/Tractor' ? 'TRUCK' : 'TRAILER'
    loadCategory(`${prefix}_MODEL_${normaliseToKey(make)}`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded, make, vehicleType])

  // ── Effect 2b: auto-select "Not in List" model once NOT_IN_LIST_MODEL loads ──
  // Effect 2 triggers the category load asynchronously. This effect fires when
  // the cache entry arrives and sets the model field to the "Not in List" label
  // from ref_master so the stored value carries the real numeric ID at save time.
  const notInListModelCache = refCache[REF_KEYS.NOT_IN_LIST_MODEL]
  useEffect(() => {
    if (sectionId !== 'standard-equipment') return
    if (make.toLowerCase() !== 'not in list') return
    if (!notInListModelCache?.length) return
    const option = notInListModelCache.find(o => o.label.toLowerCase() === 'not in list')
    if (!option) return
    const currentModel = useValuationStore.getState().formValues['standard-equipment']?.['model'] ?? ''
    if (currentModel === option.label) return
    setFieldValue('standard-equipment', 'model', option.label)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionId, make, notInListModelCache])

  // ── Effect 3: dependent load — Engine Model depends on Engine Mfg ────────
  const prevEngineMfgRef = useRef<string | null>(null)
  useEffect(() => {
    if (!isExpanded || sectionId !== 'standard-equipment') return
    if (vehicleType !== 'Truck/Tractor') return
    // Engine Mfg actually changed — clear the stale Engine Model value.
    if (prevEngineMfgRef.current !== null && prevEngineMfgRef.current !== engineMfg) {
      setFieldValue('standard-equipment', 'engineModel', '')
    }
    prevEngineMfgRef.current = engineMfg
    const isValidMfg = engineMfg && engineMfg !== 'Select'
    if (!isValidMfg) return
    if (engineMfg.toLowerCase() === 'not in list') {
      setFieldValue('standard-equipment', 'engineModel', 'Not in List')
      return
    }
    loadCategory(`TRUCK_ENGINE_MODEL_${normaliseToKey(engineMfg)}`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded, engineMfg, vehicleType])

  // ── Effect 3b: dependent load — # of Speeds depends on Trans Mfg ─────────
  const prevTransMfgRef = useRef<string | null>(null)
  useEffect(() => {
    if (!isExpanded || sectionId !== 'standard-equipment') return
    if (vehicleType !== 'Truck/Tractor') return
    // Trans Mfg actually changed — clear the stale # of Speeds value.
    if (prevTransMfgRef.current !== null && prevTransMfgRef.current !== transMfg) {
      setFieldValue('standard-equipment', 'noOfSpeeds', '')
    }
    prevTransMfgRef.current = transMfg
    const isValidMfg = transMfg && transMfg !== 'Select'
    if (!isValidMfg) return
    if (transMfg.toLowerCase() === 'not in list') {
      // Load the shared NOT_IN_LIST_MODEL category (reused as a generic "Not in
      // List" sentinel) so # of Speeds gets a real ref_master ID rather than a
      // hardcoded string (numSpeeds is ID-backed, same as model). Auto-selection
      // happens in the effect below once the category arrives in the cache.
      loadCategory(REF_KEYS.NOT_IN_LIST_MODEL)
      return
    }
    loadCategory(`TRUCK_TRANS_SPEED_${normaliseToKey(transMfg)}`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded, transMfg, vehicleType])

  // ── Effect 3c: auto-select "Not in List" # of Speeds once the shared category loads ──
  // Mirrors Effect 2b for model — fires when the cache entry arrives and sets the
  // field to the "Not in List" label from ref_master so the stored value carries
  // the real numeric ID at save time.
  const notInListSpeedCache = refCache[REF_KEYS.NOT_IN_LIST_MODEL]
  useEffect(() => {
    if (sectionId !== 'standard-equipment') return
    if (transMfg.toLowerCase() !== 'not in list') return
    if (!notInListSpeedCache?.length) return
    const option = notInListSpeedCache.find(o => o.label.toLowerCase() === 'not in list')
    if (!option) return
    const currentSpeed = useValuationStore.getState().formValues['standard-equipment']?.['noOfSpeeds'] ?? ''
    if (currentSpeed === option.label) return
    setFieldValue('standard-equipment', 'noOfSpeeds', option.label)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionId, transMfg, notInListSpeedCache])

  // ── Effect 4: reset body-info (+ Subtype) when Use/Type changes, and fetch Subtype
  // options live so we can determine whether Use/Type has Subtypes at all.
  // Separate refs per effect so each independently skips its own mount run.
  const prevUseRef = useRef<string | null>(null)
  const prevTypeRef = useRef<string | null>(null)

  useEffect(() => {
    // Skip mount: record initial value without any side-effects
    if (prevUseRef.current === null) {
      prevUseRef.current = useValue
      return
    }
    // Skip if value hasn't actually changed
    if (prevUseRef.current === useValue) return
    prevUseRef.current = useValue

    // Reset body-info field values (+ the now-stale Subtype selection) whenever Use
    // genuinely changes — but not while an existing claim's data is loading in
    // (loadClaimForEditing), where the '' → loaded transition on this same tick is
    // the claim's own value arriving, not a user edit.
    if (!useValuationStore.getState().isLoadingClaim) {
      if (sectionId === 'body-information') resetSectionFields('body-information')
      if (sectionId === 'standard-equipment') setFieldValue('standard-equipment', 'useSubtype', '')
    }

    // Live-lookup Subtype values for the newly selected Use (cache-first on the backend).
    // Gated to a single section's hook instance — useConditionalFields runs once per
    // mounted section, and this effect's dependency (useValue) is form-wide, so without
    // this guard every section would independently re-fire the same VVS call.
    if (sectionId === 'standard-equipment' && vehicleType === 'Truck/Tractor' && isValidUse) {
      loadUsageSubtypes('TT', useValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useValue])

  useEffect(() => {
    // Skip mount: record initial value without any side-effects
    if (prevTypeRef.current === null) {
      prevTypeRef.current = typeValue
      return
    }
    // Skip if value hasn't actually changed
    if (prevTypeRef.current === typeValue) return
    prevTypeRef.current = typeValue

    // Reset body-info field values (+ the now-stale Subtype selection) whenever Type
    // genuinely changes — but not while an existing claim's data is loading in
    // (loadClaimForEditing), where the '' → loaded transition on this same tick is
    // the claim's own value arriving, not a user edit.
    if (!useValuationStore.getState().isLoadingClaim) {
      if (sectionId === 'body-information') resetSectionFields('body-information')
      if (sectionId === 'standard-equipment') setFieldValue('standard-equipment', 'typeSubtype', '')
    }

    // Live-lookup Subtype values for the newly selected Type (cache-first on the backend).
    // Gated to a single section's hook instance — see the matching comment in the Use effect above.
    if (sectionId === 'standard-equipment' && vehicleType === 'Trailer' && isValidType) {
      loadUsageSubtypes('TR', typeValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeValue])

  // ── Effect 4b: reset body-info when Subtype changes (once Use/Type is known to have one) ──
  const prevUseSubtypeRef = useRef<string | null>(null)
  const prevTypeSubtypeRef = useRef<string | null>(null)

  useEffect(() => {
    if (prevUseSubtypeRef.current === null) {
      prevUseSubtypeRef.current = useSubtypeValue
      return
    }
    if (prevUseSubtypeRef.current === useSubtypeValue) return
    prevUseSubtypeRef.current = useSubtypeValue

    if (sectionId === 'body-information' && !useValuationStore.getState().isLoadingClaim) {
      resetSectionFields('body-information')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useSubtypeValue])

  useEffect(() => {
    if (prevTypeSubtypeRef.current === null) {
      prevTypeSubtypeRef.current = typeSubtypeValue
      return
    }
    if (prevTypeSubtypeRef.current === typeSubtypeValue) return
    prevTypeSubtypeRef.current = typeSubtypeValue

    if (sectionId === 'body-information' && !useValuationStore.getState().isLoadingClaim) {
      resetSectionFields('body-information')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeSubtypeValue])

  // ── Effect 4c: load attribute definitions for Body Information ───────────
  // Waits for the Subtype check to resolve (hasUseSubtypes/hasTypeSubtypes !== null)
  // before loading: a Use/Type with no Subtypes loads TRUCK_BODY_<USE> directly; one
  // with Subtypes only loads once a Subtype is actually selected, using
  // TRUCK_BODY_<USE>_<SUBTYPE> — so stale, unscoped Body Information fields never load.
  useEffect(() => {
    if (vehicleType === 'Truck/Tractor' && isValidUse) {
      if (hasUseSubtypes === false) {
        loadAttributes(`TRUCK_BODY_${normaliseToKey(useValue)}`)
      } else if (hasUseSubtypes === true && useSubtypeValue && useSubtypeValue !== 'Select') {
        loadAttributes(`TRUCK_BODY_${normaliseToKey(useValue)}_${normaliseToKey(useSubtypeValue)}`)
      }
    } else if (vehicleType === 'Trailer' && isValidType) {
      if (hasTypeSubtypes === false) {
        loadAttributes(`TRAILER_BODY_${normaliseToKey(typeValue)}`)
      } else if (hasTypeSubtypes === true && typeSubtypeValue && typeSubtypeValue !== 'Select') {
        loadAttributes(`TRAILER_BODY_${normaliseToKey(typeValue)}_${normaliseToKey(typeSubtypeValue)}`)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleType, useValue, typeValue, hasUseSubtypes, hasTypeSubtypes, useSubtypeValue, typeSubtypeValue])

  // ── Effect 5: batch-load ref options for body-info select fields ──────────
  useEffect(() => {
    if (sectionId !== 'body-information' || !isExpanded) return
    if (vehicleType !== 'Truck/Tractor' && vehicleType !== 'Trailer') return
    if (vehicleType === 'Truck/Tractor') {
      if (!isValidUse) return
      if (hasUseSubtypes === true && (!useSubtypeValue || useSubtypeValue === 'Select')) return

      const attrCategory = hasUseSubtypes === true
        ? `TRUCK_BODY_${normaliseToKey(useValue)}_${normaliseToKey(useSubtypeValue)}`
        : `TRUCK_BODY_${normaliseToKey(useValue)}`
      const attrs = attrCache[attrCategory]
      if (!attrs?.length) return

      // Derive which fields still carry sentinel options (i.e. need ref data)
      // by running the mapper and filtering — avoids duplicating static-options logic.
      const mappedFields = mapAttributesToFields(attrs)
      const refCats = mappedFields
        .filter(
          (f) =>
            f.type === 'select' &&
            f.options?.length === 1 &&
            f.options[0] === 'Select'
        )
        .map((f) => f.refCategory ?? `${attrCategory}_${normaliseToKey(f.id)}`)
        .filter((cat) => !refCache[cat])

      if (refCats.length) loadBatch(refCats)
    } else if (vehicleType === 'Trailer') {
      if (!isValidType) return
      if (hasTypeSubtypes === true && (!typeSubtypeValue || typeSubtypeValue === 'Select')) return

      const attrCategory = hasTypeSubtypes === true
        ? `TRAILER_BODY_${normaliseToKey(typeValue)}_${normaliseToKey(typeSubtypeValue)}`
        : `TRAILER_BODY_${normaliseToKey(typeValue)}`
      const attrs = attrCache[attrCategory]
      if (!attrs?.length) return

      // Derive which fields still carry sentinel options (i.e. need ref data)
      // by running the mapper and filtering — avoids duplicating static-options logic.
      const mappedFields = mapAttributesToFields(attrs)
      const refCats = mappedFields
        .filter(
          (f) =>
            f.type === 'select' &&
            f.options?.length === 1 &&
            f.options[0] === 'Select'
        )
        .map((f) => f.refCategory ?? `${attrCategory}_${normaliseToKey(f.id)}`)
        .filter((cat) => !refCache[cat])

      if (refCats.length) loadBatch(refCats)
    }

    // attrCache in deps ensures this re-runs when attribute definitions arrive
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded, sectionId, vehicleType, useValue, typeValue, hasUseSubtypes, hasTypeSubtypes, useSubtypeValue, typeSubtypeValue, attrCache])

  // ── Memoised field resolution + augmentation ──────────────────────────────
  const augmentedFields = useMemo(() => {
    // Resolve the base field list for this section
    let baseFields: FieldDefinition[] = []

    if (sectionId === 'basic-information') {
      baseFields = SECTIONS.find((s) => s.id === 'basic-information')?.fields ?? []
    } else if (sectionId === 'standard-equipment') {
      if (vehicleType === 'Truck/Tractor') baseFields = TRUCK_STANDARD_FIELDS
      else if (vehicleType === 'Trailer') baseFields = TRAILER_STANDARD_FIELDS
      else baseFields = SECTIONS.find((s) => s.id === 'standard-equipment')?.fields ?? []
    } else if (sectionId === 'additional-options') {
      if (vehicleType === 'Truck/Tractor') baseFields = TRUCK_ADDITIONAL_FIELDS
      else if (vehicleType === 'Trailer') baseFields = TRAILER_ADDITIONAL_FIELDS
      else baseFields = SECTIONS.find((s) => s.id === 'additional-options')?.fields ?? []
    } else if (sectionId === 'vehicle-condition') {
      if (vehicleType === 'Truck/Tractor') baseFields = TRUCK_CONDITIONAL_FIELDS
      else if (vehicleType === 'Trailer') baseFields = TRAILER_CONDITIONAL_FIELDS
      else baseFields = SECTIONS.find((s) => s.id === 'vehicle-condition')?.fields ?? []
    } else if (sectionId === 'body-information') {
      if (vehicleType === 'Truck/Tractor' && isValidUse) {
        if (hasUseSubtypes === true) {
          // Subtype dropdown is the first Body Information field — its value lives in
          // standard-equipment (formSection), so Use-change resets/payload mapping are unchanged.
          const subtypeField: FieldDefinition = {
            ...TRUCK_USE_SUBTYPE_FIELD,
            refCategory: `TRUCK_USE_SUBTYPE_${normaliseToKey(useValue)}`,
          }
          const isValidUseSubtype = useSubtypeValue && useSubtypeValue !== 'Select'
          const attrCategory = `TRUCK_BODY_${normaliseToKey(useValue)}_${normaliseToKey(useSubtypeValue)}`
          const attrs = isValidUseSubtype ? attrCache[attrCategory] : undefined
          baseFields = [subtypeField, ...(attrs?.length ? mapAttributesToFields(attrs) : [])]
        } else if (hasUseSubtypes === false) {
          const attrCategory = `TRUCK_BODY_${normaliseToKey(useValue)}`
          const attrs = attrCache[attrCategory]
          baseFields = attrs?.length ? mapAttributesToFields(attrs) : []
        }
        // hasUseSubtypes === null: still checking — baseFields stays [] until resolved
      } else if (vehicleType === 'Trailer' && isValidType) {
        if (hasTypeSubtypes === true) {
          const subtypeField: FieldDefinition = {
            ...TRAILER_TYPE_SUBTYPE_FIELD,
            refCategory: `TRAILER_TYPE_SUBTYPE_${normaliseToKey(typeValue)}`,
          }
          const isValidTypeSubtype = typeSubtypeValue && typeSubtypeValue !== 'Select'
          const attrCategory = `TRAILER_BODY_${normaliseToKey(typeValue)}_${normaliseToKey(typeSubtypeValue)}`
          const attrs = isValidTypeSubtype ? attrCache[attrCategory] : undefined
          baseFields = [subtypeField, ...(attrs?.length ? mapAttributesToFields(attrs) : [])]
        } else if (hasTypeSubtypes === false) {
          const attrCategory = `TRAILER_BODY_${normaliseToKey(typeValue)}`
          const attrs = attrCache[attrCategory]
          baseFields = attrs?.length ? mapAttributesToFields(attrs) : []
        }
      }
    } else {
      baseFields = SECTIONS.find((s) => s.id === sectionId)?.fields ?? []
    }

    // Augment each field's options from the ref cache
    return baseFields.map((field) =>
      augmentFieldOptions(field, sectionId, vehicleType, deps, refCache)
    )
    // deps object is recreated each render; spread the values explicitly
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionId, vehicleType, useValue, typeValue, useSubtypeValue, typeSubtypeValue, hasUseSubtypes, hasTypeSubtypes, make, engineMfg, transMfg, refCache, attrCache])

  // ── Auto-default trimLevel to 'Standard' once options load ───────────────
  useEffect(() => {
    if (sectionId !== 'additional-options') return
    const trimField = augmentedFields.find((f) => f.id === 'trimLevel')
    if (!trimField?.options || trimField.options.length <= 1) return  // still sentinel

    const currentVal = useValuationStore.getState().formValues['additional-options']?.['trimLevel'] ?? ''
    if (currentVal && currentVal !== 'Select') return  // already set

    const standardOption = trimField.options.find((o) => o.toLowerCase() === 'standard')
    if (standardOption) setFieldValue('additional-options', 'trimLevel', standardOption, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [augmentedFields, sectionId])

  // ── Re-match pending AI values once options become available ──────────────
  // When AI mapping runs before a section is opened, options are still sentinel
  // (['Select'] only). The raw AI value is stored in fieldMetadata.rawAIValue.
  // This effect fires whenever augmentedFields changes (i.e. when the ref cache
  // populates and real options arrive), and resolves any pending matches.
  useEffect(() => {
    // Read latest store state directly to avoid stale closure issues
    const { fieldMetadata, formValues: currentFormValues } = useValuationStore.getState()
    const sectionMeta = fieldMetadata[sectionId] ?? {}

    for (const field of augmentedFields) {
      if (field.type !== 'select') continue
      const meta = sectionMeta[field.id]
      if (!meta?.rawAIValue) continue

      const realOptions = (field.options ?? []).filter((o) => !o.startsWith('Select'))
      if (realOptions.length === 0) {
        // Options not yet available (parent dependency not set) — show raw value as a
        // detected hint so the user knows a value was found, without clearing rawAIValue
        // so the re-match can run again once the parent is selected and options load.
        setScanResult(sectionId, field.id, meta.rawAIValue)
        continue
      }

      const matchResult = matchDropdownValue(
        meta.rawAIValue,
        (field.options ?? []).map((o) => ({ label: o })),
        OCR_CONFIDENCE_THRESHOLD,
      )
      const matched = matchResult.matched ? matchResult.matchedOption!.label : null
      const currentVal = currentFormValues[sectionId]?.[field.id] ?? ''

      if (matched) {
        // Apply only when the field is empty or still holds the unmatched raw value
        if (!currentVal || currentVal === meta.rawAIValue) {
          setFieldValue(sectionId, field.id, matched, true)
        }
        // Update aiValue to the resolved label so confidence badge renders correctly
        setAiValue(sectionId, field.id, matched)
        // Clear the pending hint now that a proper match has been applied
        clearScanResult(sectionId, field.id)
      } else {
        // No match found — store the raw value as a scan result hint and reset the
        // form field to "Not in List" when that option exists, otherwise empty.
        setScanResult(sectionId, field.id, meta.rawAIValue)
        const notInList = realOptions.find((o) => o.toLowerCase() === 'not in list')
        setFieldValue(sectionId, field.id, notInList ?? '', true)
      }

      // Clear rawAIValue now that options are available
      clearRawAIValue(sectionId, field.id)
    }
    // Also depends on pendingRawAIKey so the effect re-fires when a new rawAIValue
    // arrives while the section is already open and options are loaded. The key is a
    // stable string (not an object ref) so it only triggers when content actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [augmentedFields, pendingRawAIKey])

  // ── Map AI payload to body-information fields once they load ─────────────
  // body-information fields are attribute-driven and unknown at AI-processing
  // time, so PhotoStrip skips them (fields: []). When the section opens and
  // attribute_master fields arrive, re-run the mapper against the real fields,
  // apply matches, and merge them into each photo section's aiResults so that
  // the AI Process Result popup also shows body-information fields.
  useEffect(() => {
    if (sectionId !== 'body-information' || augmentedFields.length === 0) return

    const { aiRawPayloads, aiResults, applyAIFields, setAiResult } = useValuationStore.getState()
    if (Object.keys(aiRawPayloads).length === 0) return

    const bodySection = { ...SECTIONS.find((s) => s.id === 'body-information')!, fields: augmentedFields }

    for (const [photoSectionId, payload] of Object.entries(aiRawPayloads)) {
      const { mappedFields } = mapResponseToForm(payload, [bodySection])
      if (mappedFields.length === 0) continue

      // Apply to form (won't overwrite user-edited values)
      applyAIFields(mappedFields, false)

      // Merge into the photo section's aiResults so the modal shows them.
      // Deduplicate by sectionId + fieldKey — body-info fields won't already
      // be in aiResults because they were skipped during initial mapping.
      const existing = aiResults[photoSectionId] ?? []
      const existingKeys = new Set(existing.map((f) => `${f.sectionId}:${f.fieldKey}`))
      const newFields = mappedFields.filter((f) => !existingKeys.has(`${f.sectionId}:${f.fieldKey}`))
      if (newFields.length > 0) setAiResult(photoSectionId, [...existing, ...newFields])
    }
    // Re-runs whenever augmentedFields changes (attribute fields or ref options arrive)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [augmentedFields])

  return augmentedFields
}
